import { useEffect, useRef, useState, useCallback } from "react";
import Quagga from "@ericblade/quagga2";

export default function BarcodeScannerModal({ open, onClose, onDetected }) {
  const scannerRef = useRef(null);
  const lockedRef = useRef(false);
  const bufferRef = useRef([]);
  const detectedHandlerRef = useRef(null);

  const [cameraReady, setCameraReady] = useState(false);
  const [isStarted, setIsStarted] = useState(false);
  const [error, setError] = useState(null);

  // ---------------------------
  // STYLE VIDEO + CANVAS (fix iOS écran noir)
  // ---------------------------
  const applyVideoStyle = useCallback(() => {
    if (!scannerRef.current) return;

    const video = scannerRef.current.querySelector("video");
    if (video) {
      // 🔑 CRITIQUE iOS Safari : sans playsinline → écran noir
      video.setAttribute("playsinline", "true");
      video.setAttribute("muted", "true");
      video.setAttribute("autoplay", "true");
      video.style.objectFit = "cover";
      video.style.width = "100%";
      video.style.height = "100%";
      video.style.display = "block";
      video.style.position = "absolute";
      video.style.top = "0";
      video.style.left = "0";
    }

    // Rendre les canvas Quagga transparents (ils bloquent sinon l'affichage)
    const canvases = scannerRef.current.querySelectorAll("canvas");
    canvases.forEach((c) => {
      c.style.position = "absolute";
      c.style.top = "0";
      c.style.left = "0";
      c.style.width = "100%";
      c.style.height = "100%";
      c.style.opacity = "0.4";
      c.style.pointerEvents = "none";
    });
  }, []);

  // Attend que Quagga injecte la vidéo dans le DOM, puis applique les styles
  const waitForVideoAndStyle = useCallback(
    (attempt = 0) => {
      const video = scannerRef.current?.querySelector("video");
      if (video) {
        applyVideoStyle();
      } else if (attempt < 15) {
        setTimeout(() => waitForVideoAndStyle(attempt + 1), 200);
      }
    },
    [applyVideoStyle]
  );

  // ---------------------------
  // INIT QUAGGA
  // ---------------------------
  useEffect(() => {
    if (!open) return;
    if (!scannerRef.current) return;

    let cancelled = false;

    lockedRef.current = false;
    bufferRef.current = [];
    setCameraReady(false);
    setIsStarted(false);
    setError(null);
    setCameraReady(true);

    return () => {
      cancelled = true;
      try {
        Quagga.offDetected(detectedHandlerRef.current);
        Quagga.stop();
      } catch {}
      lockedRef.current = false;
      bufferRef.current = [];
    };
  }, [open, applyVideoStyle]);

  // ---------------------------
  // DETECTION LOGIC
  // ---------------------------
  const handleDetected = useCallback(
    (result) => {
      if (lockedRef.current) return;

      const code = result?.codeResult?.code;
      if (!code) return;

      const cleaned = String(code).trim();

      // Valider format EAN/UPC strict
      if (!/^\d{8,14}$/.test(cleaned)) return;

      bufferRef.current.push(cleaned);

      if (bufferRef.current.length < 3) return;

      const allSame = bufferRef.current.every((v) => v === cleaned);

      if (!allSame) {
        bufferRef.current = [];
        return;
      }

      lockedRef.current = true;
      bufferRef.current = [];

      try {
        Quagga.offDetected(detectedHandlerRef.current);
        Quagga.stop();
      } catch {}

      onDetected?.(cleaned);
      onClose?.();
    },
    [onDetected, onClose]
  );

  detectedHandlerRef.current = handleDetected;

  // ---------------------------
  // DÉMARRER LA CAMÉRA
  // ---------------------------
  const startCamera = async () => {
    try {
      Quagga.init(
        {
          inputStream: {
            type: "LiveStream",
            target: scannerRef.current,
            constraints: {
              facingMode: "environment",
              width: { ideal: 1280 },
              height: { ideal: 720 },
            },
          },
          decoder: {
            readers: [
              "ean_reader",
              "ean_8_reader",
              "upc_reader",
              "upc_e_reader",
            ],
          },
          locate: true,
        },
        async (err) => {
          if (err) {
            console.error("Quagga init error:", err);
            setError("Impossible d'accéder à la caméra.");
            return;
          }

          try {
            await Quagga.start();
            setIsStarted(true);
            Quagga.onDetected(handleDetected);

            applyVideoStyle();
            waitForVideoAndStyle();
          } catch (e) {
            console.error("Erreur caméra:", e);
            setError("Erreur au démarrage de la caméra.");
          }
        }
      );
    } catch (e) {
      console.error("Erreur caméra:", e);
      setError("Erreur au démarrage de la caméra.");
    }
  };

  if (!open) return null;

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        {/* Zone scanner */}
        <div ref={scannerRef} style={styles.scanner} />

        {/* Viseur central (décoratif) */}
        {isStarted && <div style={styles.viewfinder} />}
        {isStarted && (
          <div style={styles.scanHint}>
            Alignez le code-barres dans le cadre
          </div>
        )}

        {/* Message d'erreur */}
        {error && (
          <div style={styles.errorBanner}>
            {error}
          </div>
        )}

        {/* Bouton activer caméra */}
        {cameraReady && !isStarted && !error && (
          <button style={styles.startButton} onClick={startCamera}>
            Activer la caméra
          </button>
        )}

        {/* Bouton fermer */}
        <button style={styles.closeButton} onClick={onClose}>
          Fermer
        </button>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.92)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
  },
  modal: {
    position: "relative",
    width: "100vw",
    height: "100vh",
    overflow: "hidden",
    background: "#000",
  },
  scanner: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    overflow: "hidden",
    background: "#000",
  },
  // Viseur centré pour guider l'utilisateur
  viewfinder: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "75%",
    maxWidth: 420,
    height: 140,
    border: "3px solid rgba(255,255,255,0.95)",
    borderRadius: 16,
    zIndex: 10,
    pointerEvents: "none",
    boxShadow: "0 0 0 9999px rgba(0,0,0,0.55)",
  },
  startButton: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    zIndex: 20,
    padding: "16px 32px",
    background: "#ffffff",
    border: "none",
    borderRadius: 14,
    fontWeight: "700",
    fontSize: 17,
    cursor: "pointer",
    whiteSpace: "nowrap",
    boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
  },
  closeButton: {
    position: "absolute",
    bottom: 32,
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 20,
    padding: "16px 0",
    background: "rgba(255,255,255,0.12)",
    border: "1px solid rgba(255,255,255,0.35)",
    borderRadius: 14,
    color: "#fff",
    fontSize: 16,
    fontWeight: "600",
    width: "85%",
    maxWidth: 420,
    cursor: "pointer",
    backdropFilter: "blur(10px)",
  },
  scanHint: {
    position: "absolute",
    top: "calc(50% + 95px)",
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 20,
    color: "#fff",
    fontSize: 15,
    fontWeight: "500",
    background: "rgba(0,0,0,0.45)",
    padding: "10px 14px",
    borderRadius: 999,
    backdropFilter: "blur(6px)",
  },
  errorBanner: {
    position: "absolute",
    top: 20,
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 20,
    padding: "12px 20px",
    background: "rgba(220, 53, 69, 0.9)",
    color: "#fff",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: "500",
    maxWidth: "80%",
    textAlign: "center",
  },
};